import {
  FilterCategory,
  IndexedFilters,
  IndexedNestedFilter,
} from '@/catalog/types/filters';
import FilterOptionMainCheckbox from './filter-option-main-checkbox';
import FilterOptionCheckboxContainer from '../filter-option-checkbox-group/filter-option-checkbox-container';
import { get, keys, omit } from 'lodash-es';
import { NestedFilterCounts } from '@/catalog/types/catalog-market-loader';
import { useNestedCheckboxToggles } from '@/catalog/hooks/use-nested-checkbox-toggles';

interface FilterOptionNestedCheckboxGroupProps {
  /** The category name of the nested filter, e.g. `makes` */
  categoryName: FilterCategory;
  /** Object map of all available main filters to their associated sub-filters, e.g. `{ 'BMW': ['X3', '330i'] }` */
  categoryFilters: IndexedNestedFilter;
  categoryFilterCount?: {
    [key: string]: NestedFilterCounts;
  };
  /** Object map of currently selected main and sub-filters, e.g. `{ }` */
  selectedFilters?: IndexedNestedFilter;
  /** Function to update the state of selected filters. */
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
}

export default function FilterOptionNestedCheckboxGroup({
  categoryName,
  categoryFilters,
  categoryFilterCount,
  selectedFilters,
  setSelectedFilters,
}: FilterOptionNestedCheckboxGroupProps) {
  // Get toggle functions for this nested checkbox group.
  const { toggleMainCheckbox, toggleSubCheckbox } = useNestedCheckboxToggles({
    categoryName,
    categoryFilters,
    setSelectedFilters,
  });

  return (
    <FilterOptionCheckboxContainer>
      {Object.entries(categoryFilters).map(([mainFilter, subFilters]) => {
        // Note: It's more performance to calculate checked at this level rather than
        // selectedFilters re-rendering FilterOptionMainCheckbox with every change.
        const checked =
          selectedFilters !== undefined && selectedFilters[mainFilter] !== undefined;
        const subFiltersTitle = categoryName === 'makes' ? 'Models' : undefined;

        const mainCount = get(categoryFilterCount, [mainFilter, 'count'], undefined);

        // Access the second-level object dynamically, excluding 'count'
        const subCount = categoryFilterCount
          ? Object.fromEntries(
              subFilters.map((sub) => [
                sub,
                get(
                  categoryFilterCount,
                  [
                    mainFilter, // Access main category
                    keys(omit(get(categoryFilterCount, mainFilter, {}), 'count'))[0], // Get dynamic nested key that is not `count`
                    sub, // Access sub-filter count
                  ],
                  0 // Fallback value if path doesn't exist
                ),
              ])
            )
          : undefined;

        return (
          <FilterOptionMainCheckbox
            key={mainFilter}
            mainFilter={mainFilter}
            mainFilterCount={mainCount}
            subFilters={subFilters}
            subFiltersCount={subCount}
            checked={checked}
            selectedSubFilters={selectedFilters?.[mainFilter]}
            toggleMainCheckbox={toggleMainCheckbox}
            toggleSubCheckbox={toggleSubCheckbox}
            subFiltersTitle={subFiltersTitle}
          />
        );
      })}
    </FilterOptionCheckboxContainer>
  );
}
